import { useQuery } from "@tanstack/react-query"
import { useRef } from "react"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { monto } from "@/componentes/tesoreria/dinero"
import { Cajon } from "@/componentes/ui/Cajon"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { comoFecha } from "@/lib/fechas"

type Movimiento = {
  id: number
  fecha: string
  entrada: boolean
  concepto: string
  referencia: string | null
  expediente: string | null
  monto: string
  anulado: boolean
  saldo_despues: string | null
}

type Ficha = { saldo: string | null; movimientos: Movimiento[]; aviso: string | null }

const TACHADO = { textDecoration: "line-through" as const }

/**
 * La consulta de un banco desde el Resumen Macro (30/10, pedido del usuario): su saldo y sus
 * últimos 100 movimientos, en una ficha como la de las órdenes de pago.
 *
 * ⚠ **Sólo se mira.** Ni registrar, ni editar, ni anular, ni soportes: eso sigue en Tesorería.
 * La ruta es de lectura (`services/movimientos_banco.py`).
 *
 * ⚠ **Sin PIN, por decisión del usuario, y sólo para gerencia** (30/10): lo protege el permiso
 * `resumen_macro:movimientos`, no `resumen_macro:ver`.
 */
export function FichaBanco({
  origen,
  cuentaId,
  nombre,
  moneda,
  saldoLista,
  nota,
  onCerrar,
}: {
  origen: string
  cuentaId: number
  nombre: string
  moneda: string
  saldoLista: string
  nota: string | null
  onCerrar: () => void
}) {
  const tabla = useRef<HTMLDivElement>(null)
  const consulta = useQuery({
    queryKey: ["resumen-macro", "banco", origen, cuentaId],
    queryFn: () =>
      api.get<Ficha>(`/api/v1/resumen-macro/bancos/${cuentaId}/movimientos?origen=${origen}`),
    retry: false,
  })
  const ficha = consulta.data
  const saldo = ficha?.saldo ?? saldoLista

  return (
    <Cajon abierto titulo={nombre} onCerrar={onCerrar} ancho="amplio">
      <div className="space-y-4">
        <div className="tarjeta flex flex-wrap items-start justify-between gap-3 p-4">
          <div>
            <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
              Saldo de la cuenta
            </p>
            <p className="text-2xl font-semibold tabular-nums">
              {monto(saldo)} {moneda}
            </p>
            {nota && (
              <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                {nota}
                {ficha?.saldo && monto(ficha.saldo) !== monto(saldoLista)
                  ? ` · en el resumen: ${monto(saldoLista)} ${moneda}`
                  : ""}
              </p>
            )}
          </div>
          {ficha && ficha.movimientos.length > 0 && (
            <Exportar
              titulo={`Movimientos de ${nombre}`}
              subtitulo={`Últimos ${ficha.movimientos.length} · Saldo ${monto(saldo)} ${moneda}`}
              contenido={tabla}
            />
          )}
        </div>

        {consulta.isPending && <EstadoCarga etiqueta="Consultando los movimientos…" />}
        {consulta.isError && (
          <EstadoError
            titulo="No se pudieron consultar los movimientos"
            error={consulta.error}
            onReintentar={() => consulta.refetch()}
          />
        )}
        {ficha?.aviso && (
          <p role="status" className="tarjeta p-3 text-sm" style={{ color: "var(--warning)" }}>
            No se pudieron traer los movimientos de esta cuenta: {ficha.aviso}. El saldo es el del
            resumen.
          </p>
        )}

        {ficha && !ficha.aviso && (
          <div ref={tabla} className="space-y-2">
            <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
              Últimos {ficha.movimientos.length} movimientos, del más reciente al más antiguo. Sólo
              consulta.
            </p>
            <Tabla etiqueta={`Últimos movimientos de ${nombre}`}>
              <Cabecera>
                <Th derecha>Fecha</Th>
                <Th>Concepto</Th>
                <Th derecha>Entrada</Th>
                <Th derecha>Salida</Th>
                <Th derecha>Saldo</Th>
              </Cabecera>
              <Cuerpo>
                {ficha.movimientos.length === 0 && (
                  <TablaVacia colSpan={5}>Esta cuenta no tiene movimientos.</TablaVacia>
                )}
                {ficha.movimientos.map((m) => {
                  const estilo = m.anulado ? TACHADO : undefined
                  const detalle = [
                    m.anulado ? "Anulado" : null,
                    m.referencia ? `Ref. ${m.referencia}` : null,
                    m.expediente ? `Exp. ${m.expediente}` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")
                  return (
                    <Fila key={m.id} atenuada={m.anulado}>
                      <Td derecha numero>
                        {comoFecha(m.fecha)}
                      </Td>
                      <Td>
                        <span style={estilo}>{m.concepto}</span>
                        {detalle && (
                          <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                            {detalle}
                          </span>
                        )}
                      </Td>
                      <Td derecha numero>
                        {m.entrada && (
                          <span style={{ color: "var(--success)", ...estilo }}>
                            {monto(m.monto)}
                          </span>
                        )}
                      </Td>
                      <Td derecha numero>
                        {!m.entrada && (
                          <span style={{ color: "var(--destructive)", ...estilo }}>
                            {monto(m.monto)}
                          </span>
                        )}
                      </Td>
                      <Td derecha numero>
                        {m.saldo_despues === null ? "—" : monto(m.saldo_despues)}
                      </Td>
                    </Fila>
                  )
                })}
              </Cuerpo>
            </Tabla>
          </div>
        )}
      </div>
    </Cajon>
  )
}
